/**
 * Bulk Upload — Template Generator
 *
 * Generates downloadable .xlsx template files for a given upload schema.
 * Uses ExcelJS (already a project dependency) to create:
 * - Row 1: Human-readable headers with styling and hover comments
 * - Row 2: Example data row
 * - Data validation (dropdowns) for enum fields
 * - An Instructions sheet
 */

import { Workbook, Worksheet, Fill, Font, Alignment, DataValidation } from 'exceljs';
import { UploadSchema, UploadFieldDef, LookupData } from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HEADER_FILL: Fill = {
	type: 'pattern',
	pattern: 'solid',
	fgColor: { argb: 'FF1A3C61' }, // Dark blue
};

const HEADER_FONT: Partial<Font> = {
	bold: true,
	color: { argb: 'FFFFFFFF' },
	size: 11,
};

const EXAMPLE_FILL: Fill = {
	type: 'pattern',
	pattern: 'solid',
	fgColor: { argb: 'FFF5F5F5' },
};

const CENTER_ALIGN: Partial<Alignment> = { horizontal: 'center', vertical: 'middle' };

// ---------------------------------------------------------------------------
// Example Value Generation
// ---------------------------------------------------------------------------

function getExampleValue(field: UploadFieldDef, lookupData?: LookupData): string {
	switch (field.dataType) {
		case 'string': {
			// Respect maxLength constraint for the example value
			if (field.constraints?.maxLength) {
				if (field.constraints.maxLength <= 3)
					return String(field.defaultValue ?? 'ABC').slice(0, field.constraints.maxLength);
				if (field.constraints.maxLength <= 10)
					return `Example`.slice(0, field.constraints.maxLength);
			}
			return `Example ${field.label}`;
		}
		case 'number':
			return field.constraints?.min !== undefined ? String(field.constraints.min) : '100';
		case 'date':
			return '2025-01-15';
		case 'boolean':
			return 'Yes';
		case 'enum':
			if (field.defaultValue !== undefined) return String(field.defaultValue);
			return field.enumValues?.[0] ?? '';
		case 'relation': {
			if (field.relation && lookupData) {
				const items = lookupData[field.relation.contextKey];
				if (items && items.length > 0) {
					return String(items[0][field.relation.matchField] ?? items[0].name);
				}
			}
			return `[${field.label} name]`;
		}
		default:
			return '';
	}
}

function getFieldHint(field: UploadFieldDef): string {
	const lines: string[] = [];

	// Required / Optional status (most important — first line)
	lines.push(field.required ? '⚠ REQUIRED' : '○ Optional');

	// Data type
	const typeLabel = field.dataType.charAt(0).toUpperCase() + field.dataType.slice(1);
	lines.push(`Type: ${typeLabel}`);

	// Enum options
	if (field.dataType === 'enum' && field.enumValues) {
		lines.push(`Allowed values: ${field.enumValues.join(', ')}`);
	}

	// Relation / lookup info
	if (field.dataType === 'relation' && field.relation) {
		lines.push(
			`Lookup: matched against ${field.relation.entityType} by ${field.relation.matchField}`
		);
	}

	// Constraints
	if (field.constraints?.min !== undefined) lines.push(`Min value: ${field.constraints.min}`);
	if (field.constraints?.max !== undefined) lines.push(`Max value: ${field.constraints.max}`);
	if (field.constraints?.maxLength) lines.push(`Max length: ${field.constraints.maxLength} chars`);

	// Description if available
	if (field.description) lines.push(`\n${field.description}`);

	return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Worksheet Creation
// ---------------------------------------------------------------------------

function createDataSheet(
	workbook: Workbook,
	schema: UploadSchema,
	lookupData?: LookupData
): Worksheet {
	const ws = workbook.addWorksheet(schema.label, {
		properties: { defaultColWidth: 18 },
	});

	const fields = schema.fields;

	// Row 1: Headers
	const headerRow = ws.addRow(fields.map((f) => `${f.label}${f.required ? ' *' : ''}`));
	headerRow.eachCell((cell, colNum) => {
		cell.fill = HEADER_FILL;
		cell.font = HEADER_FONT;
		cell.alignment = CENTER_ALIGN;
		// Add a comment with field info
		const field = fields[colNum - 1];
		if (field) {
			cell.note = getFieldHint(field);
		}
	});
	headerRow.height = 24;

	// Row 2: Example data row
	const exampleRow = ws.addRow(fields.map((f) => getExampleValue(f, lookupData)));
	exampleRow.eachCell((cell) => {
		cell.fill = EXAMPLE_FILL;
		cell.font = { italic: true, color: { argb: 'FF888888' } };
	});

	// Data validation for enum fields
	for (let i = 0; i < fields.length; i++) {
		const field = fields[i];
		const colLetter = getColumnLetter(i + 1);

		if (field.dataType === 'enum' && field.enumValues && field.enumValues.length > 0) {
			const validation: DataValidation = {
				type: 'list',
				allowBlank: !field.required,
				formulae: [`"${field.enumValues.join(',')}"`],
				showErrorMessage: true,
				errorTitle: 'Invalid Value',
				error: `Must be one of: ${field.enumValues.join(', ')}`,
			};
			// Apply to rows 3-1000 (data rows)
			for (let row = 3; row <= 1000; row++) {
				ws.getCell(`${colLetter}${row}`).dataValidation = validation;
			}
		}

		if (field.dataType === 'boolean') {
			const validation: DataValidation = {
				type: 'list',
				allowBlank: !field.required,
				formulae: ['"Yes,No"'],
				showErrorMessage: true,
				errorTitle: 'Invalid Value',
				error: 'Must be Yes or No',
			};
			for (let row = 3; row <= 1000; row++) {
				ws.getCell(`${colLetter}${row}`).dataValidation = validation;
			}
		}
	}

	// Auto-fit columns based on header length
	ws.columns.forEach((col, i) => {
		const field = fields[i];
		if (field) {
			const labelLen = field.label.length + (field.required ? 2 : 0);
			col.width = Math.max(labelLen + 4, 15);
		}
	});

	// Freeze header row
	ws.views = [{ state: 'frozen', ySplit: 1, xSplit: 0, topLeftCell: 'A2' }];

	return ws;
}

function createInstructionsSheet(workbook: Workbook, schema: UploadSchema): Worksheet {
	const ws = workbook.addWorksheet('Instructions', {
		properties: { defaultColWidth: 30 },
	});

	const titleRow = ws.addRow([`Bulk Upload Template — ${schema.label}`]);
	titleRow.font = { bold: true, size: 14 };
	ws.addRow([]);

	ws.addRow(['General Instructions']);
	ws.getRow(ws.rowCount).font = { bold: true, size: 12 };
	ws.addRow(['1. Enter your data starting from row 2 (or overwrite the example/mock data rows).']);
	ws.addRow(['2. Do NOT modify row 1 (headers).']);
	ws.addRow(['3. Fields marked with * are required.']);
	ws.addRow(['4. Columns with dropdown validation will restrict input to valid values.']);
	ws.addRow(['5. For date fields, use YYYY-MM-DD format (e.g., 2025-01-15).']);
	ws.addRow([
		'6. For relation fields (e.g., Work Plan, Component), enter the name exactly as it appears in the system.',
	]);
	ws.addRow(['7. The upload wizard will help you resolve any name mismatches interactively.']);

	ws.addRow([]);
	ws.addRow(['Field Reference']);
	ws.getRow(ws.rowCount).font = { bold: true, size: 12 };
	ws.addRow([]);

	// Field reference table
	const refHeader = ws.addRow(['Field', 'Type', 'Required', 'Details']);
	refHeader.font = { bold: true };
	refHeader.eachCell((cell) => {
		cell.fill = HEADER_FILL;
		cell.font = HEADER_FONT;
	});

	for (const field of schema.fields) {
		const details: string[] = [];
		if (field.dataType === 'enum' && field.enumValues) {
			details.push(`Options: ${field.enumValues.join(', ')}`);
		}
		if (field.dataType === 'relation' && field.relation) {
			details.push(`References: ${field.relation.entityType}`);
		}
		if (field.constraints?.min !== undefined) details.push(`Min: ${field.constraints.min}`);
		if (field.constraints?.max !== undefined) details.push(`Max: ${field.constraints.max}`);
		if (field.constraints?.maxLength) details.push(`Max length: ${field.constraints.maxLength}`);
		if (field.defaultValue !== undefined) details.push(`Default: ${field.defaultValue}`);

		ws.addRow([
			field.label,
			field.dataType,
			field.required ? 'Yes' : 'No',
			details.join('; ') || '—',
		]);
	}

	// Widen columns
	ws.getColumn(1).width = 25;
	ws.getColumn(2).width = 12;
	ws.getColumn(3).width = 10;
	ws.getColumn(4).width = 60;

	return ws;
}

/**
 * If lookup data is provided for relation fields, create a hidden reference sheet
 * with all valid lookup values (helps with data-entry auto-complete in Excel).
 */
function createLookupSheet(
	workbook: Workbook,
	schema: UploadSchema,
	lookupData: LookupData
): Worksheet | null {
	const relationFields = schema.fields.filter((f) => f.dataType === 'relation' && f.relation);
	if (relationFields.length === 0) return null;

	const ws = workbook.addWorksheet('Reference Data', {
		state: 'veryHidden', // Users won't see this unless they unhide
	});

	let col = 1;
	for (const field of relationFields) {
		const rel = field.relation;
		if (!rel) continue;
		const items = lookupData[rel.contextKey];
		if (!items || items.length === 0) continue;

		// Header
		ws.getCell(1, col).value = field.label;
		ws.getCell(1, col).font = { bold: true };

		// Values
		for (let i = 0; i < items.length; i++) {
			ws.getCell(i + 2, col).value = String(items[i][rel.matchField] ?? items[i].name);
		}

		col++;
	}

	return col > 1 ? ws : null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface GenerateTemplateOptions {
	schema: UploadSchema;
	/** Lookup data for relation fields — used to generate example values & reference sheet */
	lookupData?: LookupData;
	/** Custom filename (without extension) */
	filename?: string;
}

/**
 * Generate a bulk upload template workbook for a given schema.
 * Returns an ExcelJS Workbook ready to be written to a buffer.
 */
export function generateTemplateWorkbook(options: GenerateTemplateOptions): Workbook {
	const { schema, lookupData } = options;
	const workbook = new Workbook();

	workbook.creator = 'BLDT Bulk Upload';
	workbook.created = new Date();

	createDataSheet(workbook, schema, lookupData);
	createInstructionsSheet(workbook, schema);
	if (lookupData) {
		createLookupSheet(workbook, schema, lookupData);
	}

	return workbook;
}

/**
 * Generate a template and trigger a browser download.
 */
export async function downloadTemplate(options: GenerateTemplateOptions): Promise<void> {
	const workbook = generateTemplateWorkbook(options);
	const buffer = await workbook.xlsx.writeBuffer();

	const filename = options.filename ?? `${options.schema.entityType}-upload-template`;
	const blob = new Blob([buffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});

	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${filename}.xlsx`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getColumnLetter(colNum: number): string {
	let result = '';
	let n = colNum;
	while (n > 0) {
		n--;
		result = String.fromCharCode(65 + (n % 26)) + result;
		n = Math.floor(n / 26);
	}
	return result;
}
